const QR_VERSION = 5;
const QR_SIZE = 17 + QR_VERSION * 4;
const DATA_CODEWORDS = 108;
const ECC_CODEWORDS = 26;
const ALIGNMENT_POSITIONS = [6, 30];

function appendBits(bits, value, length) {
  for (let i = length - 1; i >= 0; i -= 1) {
    bits.push((value >>> i) & 1);
  }
}

function utf8Bytes(text) {
  if (typeof TextEncoder !== "undefined") {
    return Array.from(new TextEncoder().encode(text));
  }
  const encoded = encodeURIComponent(text);
  const bytes = [];
  for (let i = 0; i < encoded.length; i += 1) {
    if (encoded[i] === "%") {
      bytes.push(parseInt(encoded.slice(i + 1, i + 3), 16));
      i += 2;
    } else {
      bytes.push(encoded.charCodeAt(i));
    }
  }
  return bytes;
}

function buildDataCodewords(text) {
  const bytes = utf8Bytes(text);
  if (bytes.length > DATA_CODEWORDS - 2) {
    throw new Error("QR payload is too long.");
  }

  const bits = [];
  appendBits(bits, 0b0100, 4);
  appendBits(bits, bytes.length, 8);
  bytes.forEach((byte) => appendBits(bits, byte, 8));

  const capacityBits = DATA_CODEWORDS * 8;
  appendBits(bits, 0, Math.min(4, capacityBits - bits.length));
  while (bits.length % 8) bits.push(0);

  const codewords = [];
  for (let i = 0; i < bits.length; i += 8) {
    codewords.push(bits.slice(i, i + 8).reduce((acc, bit) => (acc << 1) | bit, 0));
  }
  for (let pad = 0; codewords.length < DATA_CODEWORDS; pad += 1) {
    codewords.push(pad % 2 === 0 ? 0xec : 0x11);
  }
  return codewords;
}

const EXP = Array(512).fill(0);
const LOG = Array(256).fill(0);
let gfValue = 1;
for (let i = 0; i < 255; i += 1) {
  EXP[i] = gfValue;
  LOG[gfValue] = i;
  gfValue <<= 1;
  if (gfValue & 0x100) gfValue ^= 0x11d;
}
for (let i = 255; i < 512; i += 1) EXP[i] = EXP[i - 255];

function gfMul(a, b) {
  if (!a || !b) return 0;
  return EXP[LOG[a] + LOG[b]];
}

function reedSolomonGenerator(degree) {
  let result = [1];
  for (let i = 0; i < degree; i += 1) {
    const next = Array(result.length + 1).fill(0);
    result.forEach((coef, j) => {
      next[j] ^= gfMul(coef, EXP[i]);
      next[j + 1] ^= coef;
    });
    result = next;
  }
  return result;
}

function buildErrorCorrection(data) {
  const generator = reedSolomonGenerator(ECC_CODEWORDS);
  const remainder = Array(ECC_CODEWORDS).fill(0);
  data.forEach((word) => {
    const factor = word ^ remainder.shift();
    remainder.push(0);
    generator.forEach((coef, i) => {
      remainder[i] ^= gfMul(coef, factor);
    });
  });
  return remainder;
}

function createMatrix() {
  return {
    modules: Array.from({ length: QR_SIZE }, () => Array(QR_SIZE).fill(false)),
    reserved: Array.from({ length: QR_SIZE }, () => Array(QR_SIZE).fill(false)),
  };
}

function setFunction(matrix, x, y, dark) {
  if (x < 0 || y < 0 || x >= QR_SIZE || y >= QR_SIZE) return;
  matrix.modules[y][x] = dark;
  matrix.reserved[y][x] = true;
}

function drawFinder(matrix, x, y) {
  for (let dy = -1; dy <= 7; dy += 1) {
    for (let dx = -1; dx <= 7; dx += 1) {
      const xx = x + dx;
      const yy = y + dy;
      const dark = dx >= 0 && dx <= 6 && dy >= 0 && dy <= 6
        && (dx === 0 || dx === 6 || dy === 0 || dy === 6 || (dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4));
      setFunction(matrix, xx, yy, dark);
    }
  }
}

function drawAlignment(matrix, cx, cy) {
  for (let dy = -2; dy <= 2; dy += 1) {
    for (let dx = -2; dx <= 2; dx += 1) {
      setFunction(matrix, cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
    }
  }
}

function drawFunctionPatterns(matrix) {
  drawFinder(matrix, 0, 0);
  drawFinder(matrix, QR_SIZE - 7, 0);
  drawFinder(matrix, 0, QR_SIZE - 7);

  for (let i = 8; i < QR_SIZE - 8; i += 1) {
    const dark = i % 2 === 0;
    setFunction(matrix, i, 6, dark);
    setFunction(matrix, 6, i, dark);
  }

  for (const x of ALIGNMENT_POSITIONS) {
    for (const y of ALIGNMENT_POSITIONS) {
      if (matrix.reserved[y]?.[x]) continue;
      drawAlignment(matrix, x, y);
    }
  }

  setFunction(matrix, 8, QR_VERSION * 4 + 9, true);
  drawFormatBits(matrix, 0);
}

function formatBits(mask) {
  const data = (0b01 << 3) | mask;
  let bits = data << 10;
  for (let i = 14; i >= 10; i -= 1) {
    if ((bits >>> i) & 1) bits ^= 0x537 << (i - 10);
  }
  return (((data << 10) | bits) ^ 0x5412) & 0x7fff;
}

function drawFormatBits(matrix, mask) {
  const bits = formatBits(mask);
  const bit = (i) => Boolean((bits >>> i) & 1);

  for (let i = 0; i <= 5; i += 1) setFunction(matrix, 8, i, bit(i));
  setFunction(matrix, 8, 7, bit(6));
  setFunction(matrix, 8, 8, bit(7));
  setFunction(matrix, 7, 8, bit(8));
  for (let i = 9; i < 15; i += 1) setFunction(matrix, 14 - i, 8, bit(i));

  for (let i = 0; i < 8; i += 1) setFunction(matrix, QR_SIZE - 1 - i, 8, bit(i));
  for (let i = 8; i < 15; i += 1) setFunction(matrix, 8, QR_SIZE - 15 + i, bit(i));
}

function maskBit(x, y) {
  return (x + y) % 2 === 0;
}

function drawData(matrix, codewords) {
  const bits = [];
  codewords.forEach((word) => appendBits(bits, word, 8));
  let bitIndex = 0;
  let upward = true;

  for (let right = QR_SIZE - 1; right >= 1; right -= 2) {
    if (right === 6) right -= 1;
    for (let vert = 0; vert < QR_SIZE; vert += 1) {
      const y = upward ? QR_SIZE - 1 - vert : vert;
      for (let dx = 0; dx < 2; dx += 1) {
        const x = right - dx;
        if (matrix.reserved[y][x]) continue;
        const raw = bitIndex < bits.length ? Boolean(bits[bitIndex]) : false;
        matrix.modules[y][x] = raw !== maskBit(x, y);
        bitIndex += 1;
      }
    }
    upward = !upward;
  }
}

function matrixToSvgDataUrl(matrix, title) {
  const quiet = 4;
  const size = QR_SIZE + quiet * 2;
  const rects = [];
  matrix.modules.forEach((row, y) => {
    row.forEach((dark, x) => {
      if (dark) rects.push(`<rect x="${x + quiet}" y="${y + quiet}" width="1" height="1"/>`);
    });
  });
  const safeTitle = String(title || "Public onboarding QR").replace(/[<>&"]/g, "");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges" role="img" aria-label="${safeTitle}"><rect width="100%" height="100%" fill="#f8fafc"/><g fill="#0f172a">${rects.join("")}</g></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

export function qrCodeSvgDataUrl(text, title) {
  const data = buildDataCodewords(String(text || ""));
  const ecc = buildErrorCorrection(data);
  const matrix = createMatrix();
  drawFunctionPatterns(matrix);
  drawData(matrix, data.concat(ecc));
  return matrixToSvgDataUrl(matrix, title);
}
